import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translations from '@translationKeys'
import { Nullable } from '@types'

import { POneEvent } from '@modules/hub/types'
import { Close } from '@mui/icons-material'

import ArrowLeft from '../ArrowLeft'
import ArrowRight from '../ArrowRight'
import {
  EventTile,
  EventTileEventPartneringLabel,
  EventTileGoToLabel,
  EventTileLabelArrow,
  EventTileLabelContainer,
  EventTileLabelText,
} from '../TopSection/index.styles'
import { ArrowBox, PopupCloseButtonBox, PopupContent, PopupDimmer, PopupInner, PopupRoot } from './index.styles'

const SCROLL_VALUE = 388 + 24
const MAX_TILES_WITHOUT_SCROLLING = 3
/**
 * This padding is here to ensure that the scrolling arrows on the left
 * and right side of the tab container are hidden and not blocking the
 * right and left-most tabs once the scroll enters this threshold.
 */
const HORIZONTAL_PADDING_THRESHOLD = 20

interface PastEventsPopupProps {
  eventsList: (POneEvent & { error?: string })[]
  onClose: () => void
}

const PastEventsPopup = ({ eventsList, onClose }: PastEventsPopupProps) => {
  const { t } = useTranslation()

  const ref = useRef<Nullable<HTMLDivElement>>(null)

  const [isLeftArrowDisabled, setIsLeftArrowDisabled] = useState(true)
  const [isRightArrowDisabled, setIsRightArrowDisabled] = useState(false)
  const [sliderWidth, setSliderWidth] = useState(0)

  useEffect(() => {
    if (eventsList.length <= MAX_TILES_WITHOUT_SCROLLING) {
      setIsLeftArrowDisabled(true)
      setIsRightArrowDisabled(true)
    }
  }, [eventsList])

  useEffect(() => {
    if (ref?.current) {
      setSliderWidth(Number(ref?.current?.scrollWidth || 0) - Number(ref?.current?.offsetWidth || 0))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref?.current])

  const handleScroll = (scrollOffset: number) => {
    if (ref?.current) {
      ref.current.scrollLeft += scrollOffset
    }
  }

  const onScroll = () => {
    if (ref?.current) {
      setIsLeftArrowDisabled(ref?.current?.scrollLeft < HORIZONTAL_PADDING_THRESHOLD)
      setIsRightArrowDisabled(ref?.current?.scrollLeft > sliderWidth - HORIZONTAL_PADDING_THRESHOLD)
    }
  }

  return (
    <PopupRoot data-testid="past-events-popup">
      <PopupDimmer onClick={onClose} />
      <PopupInner>
        <PopupCloseButtonBox onClick={onClose} data-testid="past-events-popup-close-button">
          <Close />
        </PopupCloseButtonBox>
        <ArrowBox
          $disabled={isLeftArrowDisabled}
          onClick={() => handleScroll(-SCROLL_VALUE)}
          data-testid="past-events-popup-left-arrow"
        >
          <ArrowLeft disabled={isLeftArrowDisabled} />
        </ArrowBox>

        <PopupContent ref={ref} onScroll={onScroll} data-testid="past-events-popup-content">
          {eventsList.map(event => (
            <EventTile
              key={event?.id}
              bannerUrl={event?.banner}
              href={event && !event?.error ? `/event/${event?.id}` : undefined}
              errorMessage={event?.error}
              name={event?.name}
              $minWidth="388px"
              $marginBottom="0"
              overlay={
                <EventTileLabelContainer>
                  <EventTileLabelText>
                    <EventTileGoToLabel>{t(translations.GO_TO)}</EventTileGoToLabel>{' '}
                    <EventTileEventPartneringLabel>{t(translations.PAST_EVENT)}</EventTileEventPartneringLabel>
                  </EventTileLabelText>
                  <EventTileLabelArrow />
                </EventTileLabelContainer>
              }
            />
          ))}
        </PopupContent>

        <ArrowBox
          $disabled={isRightArrowDisabled}
          onClick={() => handleScroll(SCROLL_VALUE)}
          data-testid="past-events-popup-right-arrow"
        >
          <ArrowRight disabled={isRightArrowDisabled} />
        </ArrowBox>
      </PopupInner>
    </PopupRoot>
  )
}

export default PastEventsPopup
